import { MaterialIcon } from './icon/icons';
import { Icon, isMaterialIcon } from './icon';
import type { MithrilVNode } from '../mithril';

export interface AssetTypeSwatchAttrs {
	icon: string;
	color: string;
	size?: number;
	/** Degrees clockwise to rotate the glyph (e.g. Valve's hourglass, turned on its side). */
	rotate?: number;
	/**
	 * Center at an exact pixel offset via absolute positioning inside a `position: relative` parent of
	 * this size (px), instead of flexbox. Flex-centering is correct in the live DOM, but `html2canvas`
	 * (used by the map print export) re-implements CSS layout itself and doesn't reliably flex-center —
	 * or correctly combine a percentage `translate()` with a `rotate()` in one `transform` — an icon-font
	 * glyph, rendering it visibly off-center in the exported PDF's map image. Computing the offset in
	 * plain pixels up front avoids both: positioning is a bare `top`/`left`, and `transform` is left to
	 * do nothing but the rotation. Only set this where the swatch is rendered inside html2canvas's
	 * capture (the map marker); every other caller keeps the default flex centering.
	 */
	centerInParentSize?: number;
}

/** Fallback glyph for an icon id that no longer resolves to a registered MaterialIcon (e.g. a removed choice). */
const FALLBACK_ICON = MaterialIcon.HELP;

export const AssetTypeSwatch = {
	view(attrs: AssetTypeSwatchAttrs): MithrilVNode {
		const { icon, color, size = 24, rotate, centerInParentSize } = attrs;
		// An empty icon means "none chosen yet" (e.g. a just-added custom row) — render nothing, rather than the
		// fallback glyph, which is reserved for a non-empty id that no longer resolves (e.g. a removed choice).
		if (icon === '') {
			return null;
		}
		const glyph = isMaterialIcon(icon) ? icon : FALLBACK_ICON;
		// Force a square box rather than relying on the glyph's own (not always square) advance width —
		// otherwise a `rotate` turns a lopsided box, visibly shifting the icon off-center.
		const style: Record<string, string> = {
			color,
			'font-size': `${size}px`,
			width: `${size}px`,
			height: `${size}px`,
			'line-height': '1',
		};
		if (centerInParentSize != null) {
			const offset = (centerInParentSize - size) / 2;
			style.position = 'absolute';
			style.top = `${offset}px`;
			style.left = `${offset}px`;
			// The box itself is centered by the offset above, but a glyph's own advance width isn't always
			// exactly `size` — without this, the glyph hugs the box's left edge instead of filling it,
			// visibly off-center within the ring.
			style['text-align'] = 'center';
		} else {
			style.display = 'inline-flex';
			style['align-items'] = 'center';
			style['justify-content'] = 'center';
		}
		if (rotate) {
			// Explicit rather than relying on the (spec-correct, but not necessarily html2canvas-correct)
			// default of 50% 50% — removes any ambiguity for html2canvas's own transform implementation.
			style.transform = `rotate(${rotate}deg)`;
			style['transform-origin'] = 'center';
		}
		return Icon.view(glyph, { style });
	},
};
